import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Check,
  Copy,
  Download,
  Loader2,
  Package,
  PackageSearch,
  Plus,
  Minus,
  Printer,
  Save,
  Trash2,
  XCircle,
} from 'lucide-react';
import { HeaderActionMenu } from '@/components/common/HeaderActionMenu';
import { Dropdown } from '@/components/common/Dropdown';
import { useDataService } from '@/lib/service-context';
import { useProBranding } from '@/lib/pro-branding';
import { useProArticles, useProCategories } from '@/hooks/use-pro-articles';
import { useProSettings } from '@/hooks/use-pro-settings';
import { DevisStatusBadge } from '@/components/common/StatusBadge';
import { formatTND } from '@/lib/format';
import { foldSearch } from '@/lib/search';
import { proErrorMessage } from '@/lib/pro-error';
import { downloadDevisPdf, printDevisPdf } from '@/lib/pdf-export';
import {
  computeAmountToPay,
  computeDeliveryFee,
  DEFAULT_DELIVERY_SETTINGS,
  summarizeProductRequests,
  type ProductRequestStatus,
  type RemiseTier,
} from '@erp/domain/pro';
import type { ProClient } from '@erp/data-access/interfaces';

const REMISE_TIERS: RemiseTier[] = ['max', 'moy', 'min'];

// Clés statiques (jamais construites par template) pour que l'audit i18n les voie.
const REQUEST_STATUS_KEY: Record<ProductRequestStatus, string> = {
  pending: 'pro.request.status_pending',
  fulfilled: 'pro.request.status_fulfilled',
  unavailable: 'pro.request.status_unavailable',
};

const REQUEST_STATUS_FALLBACK: Record<ProductRequestStatus, string> = {
  pending: 'En attente',
  fulfilled: 'Trouvé',
  unavailable: 'Indisponible',
};

const REQUEST_STATUS_STYLE: Record<ProductRequestStatus, React.CSSProperties> = {
  pending: {
    background: 'var(--pro-primary-subtle)',
    color: 'var(--pro-primary-text)',
  },
  fulfilled: { background: 'rgba(29,175,97,0.12)', color: 'var(--pro-success)' },
  unavailable: { background: 'rgba(223,28,65,0.10)', color: 'var(--pro-danger)' },
};

type EditableLine = {
  id: string;
  articleId: string;
  quantite: number;
  pttc: number; // unit price ttc — recomputed on qty change
  unitPriceTTC: number;
  remise: number;
  montantRemise: number;
  /**
   * Nom résolu SERVEUR (`fetchProArticleSnapshots`, par id, sans plafond).
   * `articleMap` ne couvre que le catalogue ProSpace : un article substitué
   * depuis le tableau de bord n'y est pas forcément publié, et la ligne
   * affichait alors son UUID brut.
   */
  articleName: string | null;
};

export function DevisDetailPage() {
  const { t } = useTranslation();
  const { code = '' } = useParams<{ code: string }>();
  const ds = useDataService();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const branding = useProBranding();

  const query = useQuery({
    queryKey: ['pro-devis', code],
    queryFn: () => ds.proDevis.getByCode(code),
    enabled: !!code,
  });

  const requestsQuery = useQuery({
    queryKey: ['pro-product-requests', code],
    queryFn: () => ds.proProductRequests.listForDevis(code),
    enabled: !!code,
  });
  const productRequests = useMemo(() => requestsQuery.data ?? [], [requestsQuery.data]);
  const requestSummary = useMemo(
    () => summarizeProductRequests(productRequests),
    [productRequests],
  );

  const articlesQuery = useProArticles();
  const categoriesQuery = useProCategories();
  const clientsQuery = useQuery({
    queryKey: ['pro-clients-list'],
    queryFn: () => ds.proClients.list(),
  });
  const proSettings = useProSettings();

  // Barème de livraison du fournisseur — MÊME source que le checkout. La RPC
  // `pro_create_commande_atomic` recalcule les frais côté serveur et refuse la
  // commande si le montant envoyé diverge : passer 0 « parce qu'un devis n'a
  // pas de livraison » faisait échouer toute transformation devis → commande.
  const deliverySettings = useMemo(
    () =>
      proSettings.data
        ? {
            fee: proSettings.data.deliveryFee,
            freeFrom: proSettings.data.freeDeliveryThreshold || null,
          }
        : DEFAULT_DELIVERY_SETTINGS,
    [proSettings.data],
  );

  const articleMap = useMemo(() => {
    const catName = new Map<string, string>();
    for (const c of categoriesQuery.data ?? []) catName.set(c.id, c.name);
    const m = new Map<
      string,
      { name: string; imageUrl: string | null; category: string; salePriceHt: number; vatRate: number }
    >();
    for (const a of articlesQuery.data ?? []) {
      m.set(a.id, {
        name: a.name,
        imageUrl: a.imageUrl,
        category: a.categoryId ? (catName.get(a.categoryId) ?? '') : '',
        salePriceHt: a.salePriceHt,
        vatRate: a.vatRate,
      });
    }
    return m;
  }, [articlesQuery.data, categoriesQuery.data]);

  // Local editable state hydrated from the fetched devis.
  const [lines, setLines] = useState<EditableLine[]>([]);
  const [clientId, setClientId] = useState<string | null>(null);
  const [remiseTier, setRemiseTier] = useState<RemiseTier>('max');
  const [showPicker, setShowPicker] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const [copied, setCopied] = useState(false);
  const [convertError, setConvertError] = useState<string | null>(null);

  // Le catalogue dépasse largement les 50 lignes affichées : sans recherche,
  // la plupart des articles étaient inatteignables depuis « Ajouter un produit ».
  const pickerResults = useMemo(() => {
    const all = articlesQuery.data ?? [];
    const q = foldSearch(pickerQuery.trim());
    const filtered = q
      ? all.filter(
          (a) =>
            foldSearch(a.name).includes(q) ||
            foldSearch(a.code ?? '').includes(q),
        )
      : all;
    return filtered.slice(0, 50);
  }, [articlesQuery.data, pickerQuery]);

  useEffect(() => {
    if (!query.data) return;
    setClientId(query.data.proClientId);
    setRemiseTier(query.data.typeRemise);
    setLines(
      (query.data.lines ?? []).map((l) => {
        const unit = l.pttc / Math.max(1, l.quantite);
        return {
          id: l.id,
          articleId: l.articleId,
          quantite: l.quantite,
          pttc: l.pttc,
          unitPriceTTC: unit,
          remise: l.remise,
          montantRemise: l.montantRemise,
          articleName: l.articleName ?? null,
        };
      }),
    );
  }, [query.data]);

  const totals = useMemo(() => {
    const pttc = lines.reduce((s, l) => s + l.pttc, 0);
    return { pttc };
  }, [lines]);

  // Detect "dirty" state vs the server payload so we only show Save when
  // the user has actually changed something.
  const dirty = useMemo(() => {
    const d = query.data;
    if (!d) return false;
    if ((d.proClientId ?? null) !== clientId) return true;
    if (d.typeRemise !== remiseTier) return true;
    const serverLines = d.lines ?? [];
    if (serverLines.length !== lines.length) return true;
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i]!;
      const s = serverLines[i];
      if (!s) return true;
      if (s.articleId !== l.articleId || s.quantite !== l.quantite) return true;
    }
    return false;
  }, [query.data, lines, clientId, remiseTier]);

  const save = useMutation({
    mutationFn: async () => {
      const d = query.data;
      if (!d) throw new Error('Devis introuvable');
      // Recompute header totals from the live local lines so the saved
      // header matches the line-item sum (no drift).
      const pttcSum = lines.reduce((s, l) => s + l.pttc, 0);
      // pht ≈ pttc / (1 + vat). We use the original devis vat ratio.
      const vatRatio = d.pttc > 0 ? d.tva / d.pttc : 0; // tva-as-fraction-of-pttc
      const pht = pttcSum * (1 - vatRatio);
      const tva = pttcSum - pht;
      return ds.proDevis.update(d.code, {
        proClientId: clientId,
        pht,
        pttc: pttcSum,
        tva,
        tauxRemise: d.tauxRemise,
        typeRemise: remiseTier,
        totalCommission: d.totalCommission,
        lines: lines.map((l) => {
          // Recompute pht from the original vat rate pulled from the article
          // (lookup by articleId via the catalog). The line `tva` field holds
          // the RATE (like the checkout path), not the amount — keep it that way
          // so every consumer reads a consistent value.
          const art = articleMap.get(l.articleId);
          const vatRate = art ? art.vatRate : 19;
          const linePht = l.pttc / (1 + vatRate / 100);
          return {
            articleId: l.articleId,
            quantite: l.quantite,
            pht: linePht,
            pttc: l.pttc,
            tva: vatRate,
            remise: l.remise,
            montantRemise: l.montantRemise,
          };
        }),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pro-devis', code] });
      qc.invalidateQueries({ queryKey: ['pro-devis-list-all'] });
    },
  });

  const cancel = useMutation({
    mutationFn: async () => {
      if (!query.data) throw new Error('Devis introuvable');
      return ds.proDevis.updateStatus(query.data.code, 'rejetee');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pro-devis', code] });
      qc.invalidateQueries({ queryKey: ['pro-devis-list-all'] });
    },
  });

  const convert = useMutation({
    onMutate: () => setConvertError(null),
    mutationFn: async () => {
      const current = query.data;
      if (!current) throw new Error(t('pro.devis.not_found', 'Devis introuvable'));
      // Le barème vient du fournisseur : commander avant qu'il soit chargé
      // enverrait le repli générique, que le serveur rejette.
      if (proSettings.isLoading) {
        throw new Error(
          t('pro.checkout.settings_loading', 'Chargement des paramètres… réessayez.'),
        );
      }
      // Les modifications non enregistrées doivent partir dans la commande —
      // sinon la ligne ajoutée à l'écran disparaît silencieusement du bon.
      const devis =
        dirty && current.status === 'en_attente' ? await save.mutateAsync() : current;

      const deliveryFee = computeDeliveryFee(devis.pttc, deliverySettings);
      const client = (clientsQuery.data ?? []).find((c) => c.id === devis.proClientId);

      return ds.proCommandes.create({
        proClientId: devis.proClientId,
        parentDevisCode: devis.code,
        pht: devis.pht,
        pttc: devis.pttc,
        tva: devis.tva,
        tauxRemise: devis.tauxRemise,
        typeRemise: devis.typeRemise,
        totalCommission: devis.totalCommission,
        deliveryFee,
        totalUsed: 0,
        // Dérivé côté serveur aussi (pttc + frais − portefeuille) : la valeur
        // envoyée doit être exactement celle-là.
        totalAPayer: computeAmountToPay(devis.pttc, deliveryFee, 0),
        usedSolde: false,
        // Adresse reprise du client du devis, pour que la commande soit
        // livrable sans ressaisie.
        deliveryAddress: client?.adresse ?? undefined,
        deliveryCity: client?.ville ?? undefined,
        deliveryPostalCode: client?.cp ?? undefined,
        deliveryComplement: client?.complement ?? undefined,
        lines: (devis.lines ?? []).map((l) => ({
          articleId: l.articleId,
          quantite: l.quantite,
          pht: l.pht,
          pttc: l.pttc,
          tva: l.tva,
          remise: l.remise,
          montantRemise: l.montantRemise,
        })),
      });
    },
    onSuccess: (cmd) => {
      qc.invalidateQueries({ queryKey: ['pro-commandes'] });
      qc.invalidateQueries({ queryKey: ['pro-wallet-balance'] });
      qc.invalidateQueries({ queryKey: ['pro-wallet-movements'] });
      navigate(`/ordres/${encodeURIComponent(cmd.code)}`);
    },
    onError: (err) => setConvertError(proErrorMessage(err, t)),
  });

  if (query.isLoading) {
    return <div className="text-[14px]" style={{ color: 'var(--pro-text-grey)' }}>…</div>;
  }
  const devis = query.data;
  if (!devis) {
    return (
      <div className="pro-card p-8 text-center">
        <p style={{ color: 'var(--pro-text-grey)' }}>
          {t('pro.devis.not_found', 'Devis introuvable')}
        </p>
        <Link
          to="/devis"
          className="inline-flex items-center gap-1 mt-3 text-[13px] font-medium"
          style={{ color: 'var(--pro-primary)' }}
        >
          <ArrowLeft className="w-4 h-4" />
          {t('pro.devis.back_list', 'Retour à la liste')}
        </Link>
      </div>
    );
  }

  // Ce qui empêche de transformer ce devis en commande. `null` = rien.
  // Un devis rejeté n'a plus à devenir un bon ; un devis dont une demande hors
  // catalogue attend encore un prix ne peut pas être chiffré (même règle que le
  // panier, qui force alors le mode « devis »).
  //
  // `banner` : le motif mérite-t-il un bandeau, ou l'écran le dit-il déjà ?
  // L'attente de chiffrage est déjà annoncée par la section « Produits
  // demandés » — inutile de la répéter en rouge. Les autres motifs ne se
  // devinent nulle part, et une infobulle ne s'ouvre pas au doigt.
  const convertBlocked: { reason: string; banner: boolean } | null = (() => {
    if (devis.status === 'rejetee') {
      return {
        reason: t('pro.devis.convert_rejected', 'Ce devis a été annulé.'),
        banner: true,
      };
    }
    if (requestSummary.hasPending) {
      return {
        reason: t(
          'pro.request.devis_only_short',
          'Impossible : un produit demandé reste à chiffrer.',
        ),
        banner: false,
      };
    }
    if (lines.length === 0) {
      return {
        reason: t('pro.devis.convert_empty', 'Ajoutez au moins un produit.'),
        banner: true,
      };
    }
    return null;
  })();
  const canConvert = convertBlocked === null;

  const setQty = (lineId: string, delta: number) => {
    setLines((prev) =>
      prev.map((l) =>
        l.id === lineId
          ? (() => {
              const q = Math.max(1, l.quantite + delta);
              return { ...l, quantite: q, pttc: l.unitPriceTTC * q };
            })()
          : l,
      ),
    );
  };
  const removeLine = (lineId: string) => setLines((prev) => prev.filter((l) => l.id !== lineId));
  const addLine = (articleId: string) => {
    const art = articleMap.get(articleId);
    if (!art) return;
    const unitTTC = art.salePriceHt * (1 + art.vatRate / 100);
    setLines((prev) => [
      ...prev,
      {
        id: `tmp-${Date.now()}-${prev.length}`,
        articleId,
        quantite: 1,
        unitPriceTTC: unitTTC,
        pttc: unitTTC,
        remise: 0,
        montantRemise: 0,
        // Ajouté depuis le catalogue Pro : le nom est déjà dans `articleMap`.
        articleName: art.name,
      },
    ]);
    setShowPicker(false);
  };

  return (
    <div className="space-y-4">
      <div className="pro-card p-6 pro-fade-in">
        {/* Header: Ref Devis + status + actions */}
        <div className="flex items-center justify-between flex-wrap gap-3 pb-5">
          <div className="flex items-center gap-3">
            <span
              className="text-[14px] font-semibold"
              style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
            >
              {t('pro.devis.ref_label', 'Ref Devis')}
            </span>
            <span
              className="text-[14px] font-semibold"
              style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
            >
              #{devis.code}
            </span>
            <DevisStatusBadge status={devis.status} />
          </div>
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            {dirty && devis.status === 'en_attente' && (
              <button
                type="button"
                onClick={() => save.mutate()}
                disabled={save.isPending}
                className="inline-flex items-center justify-center gap-2 h-9 px-3 rounded-lg text-[13px] font-medium disabled:opacity-60 flex-1 sm:flex-none"
                style={{
                  border: '1px solid var(--pro-primary)',
                  color: 'var(--pro-primary)',
                  background: 'var(--pro-primary-subtle)',
                  fontFamily: 'Figtree',
                }}
              >
                {save.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {t('pro.devis.save_changes', 'Enregistrer les modifications')}
              </button>
            )}
            <button
              type="button"
              onClick={() => convert.mutate()}
              disabled={convert.isPending || !canConvert}
              title={convertBlocked?.reason}
              className="inline-flex items-center justify-center gap-2 h-9 px-3 rounded-lg text-[13px] font-medium disabled:opacity-60 disabled:cursor-not-allowed flex-1 sm:flex-none"
              style={{
                border: '1px solid var(--pro-border)',
                color: 'var(--pro-text-dark)',
                background: 'white',
                fontFamily: 'Figtree',
              }}
            >
              {convert.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              {t('pro.devis.action_passer', 'Passer une commande')}
            </button>
            <button
              type="button"
              onClick={async () => {
                try {
                  await printDevisPdf(devis, (id) => articleMap.get(id) ?? null, {
                    company: branding.pdfCompany,
                    primaryColor: branding.pdfPrimaryColor,
                    template: branding.pdfTemplate,
                  });
                } catch (err) {
                  // eslint-disable-next-line no-console
                  console.error('PDF print failed', err);
                }
              }}
              aria-label={t('pro.doc.print', 'Imprimer')}
              title={t('pro.doc.print', 'Imprimer')}
              className="inline-flex items-center justify-center h-9 w-9 shrink-0 rounded-lg"
              style={{
                border: '1px solid var(--pro-border)',
                color: 'var(--pro-text-dark)',
                background: 'white',
              }}
            >
              <Printer className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={async () => {
                try {
                  await downloadDevisPdf(devis, (id) => articleMap.get(id) ?? null, {
                    company: branding.pdfCompany,
                    primaryColor: branding.pdfPrimaryColor,
                    template: branding.pdfTemplate,
                  });
                } catch (err) {
                  // eslint-disable-next-line no-console
                  console.error('PDF download failed', err);
                  window.print();
                }
              }}
              className="inline-flex items-center justify-center gap-2 h-9 px-3 rounded-lg text-[13px] font-medium text-white flex-1 sm:flex-none"
              style={{ background: 'var(--pro-primary)', fontFamily: 'Figtree' }}
            >
              <Download className="w-4 h-4" />
              {t('pro.devis.download', 'Télécharger')}
            </button>
            <HeaderActionMenu
              ariaLabel={t('pro.devis.more', 'Plus')}
              items={[
                {
                  key: 'copy',
                  label: copied
                    ? t('pro.devis.copied', 'Copié !')
                    : t('pro.devis.copy_code', "Copier l'identifiant"),
                  icon: copied ? Check : Copy,
                  onClick: async () => {
                    try {
                      await navigator.clipboard.writeText(devis.code);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    } catch {
                      // ignore
                    }
                  },
                },
                {
                  key: 'cancel',
                  label: t('pro.devis.cancel_devis', 'Annuler le devis'),
                  icon: XCircle,
                  danger: true,
                  disabled: devis.status !== 'en_attente' || cancel.isPending,
                  onClick: () => {
                    if (
                      confirm(
                        t(
                          'pro.devis.confirm_cancel',
                          'Annuler ce devis ? Cette action passera son statut à "Rejetée".',
                        ),
                      )
                    ) {
                      cancel.mutate();
                    }
                  },
                },
              ]}
            />
          </div>
        </div>

        {/* Échec de la transformation en commande — sans ce bandeau le bouton
            « Passer une commande » paraissait simplement inerte. */}
        {(convertError || convertBlocked?.banner) && (
          <div
            className="mb-5 p-3 rounded-xl text-[13px]"
            style={{
              background: 'rgba(223,28,65,0.08)',
              color: 'var(--pro-danger)',
              border: '1px solid rgba(223,28,65,0.2)',
            }}
          >
            {convertError ?? convertBlocked?.reason}
          </div>
        )}

        {/* Client picker */}
        <div className="pb-5 max-w-md">
          <label
            className="block text-[13px] mb-1.5"
            style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
          >
            {t('pro.devis.client', 'Client')}
          </label>
          <ClientSelect
            value={clientId}
            onChange={setClientId}
            options={clientsQuery.data ?? []}
            placeholder={t('pro.devis.select_client', 'Sélectionner un client')}
          />
        </div>

        {/* Products: mobile cards */}
        <div className="md:hidden space-y-3 pt-4" style={{ borderTop: '1px solid var(--pro-line)' }}>
          {lines.map((l) => {
            const art = articleMap.get(l.articleId);
            return (
              <div
                key={l.id}
                className="rounded-xl p-3 space-y-3"
                style={{ border: '1px solid var(--pro-line)' }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-lg shrink-0 flex items-center justify-center overflow-hidden"
                    style={{ background: 'var(--pro-grey-subtle)' }}
                  >
                    {art?.imageUrl ? (
                      <img src={art.imageUrl} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Package className="w-5 h-5" style={{ color: 'var(--pro-text-grey-light)' }} />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div
                      className="text-[14px] font-medium truncate"
                      style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
                    >
                      {art?.name ?? l.articleName ?? t('pro.devis.deleted_article', 'Article supprime')}
                    </div>
                    <div className="text-[12px] truncate" style={{ color: 'var(--pro-text-grey)' }}>
                      {formatTND(l.unitPriceTTC)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeLine(l.id)}
                    aria-label={t('pro.devis.remove_line', 'Supprimer la ligne')}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg shrink-0"
                    style={{
                      border: '1px solid var(--pro-line)',
                      color: 'var(--pro-danger)',
                      background: 'white',
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <QtyStepper qty={l.quantite} onDelta={(d) => setQty(l.id, d)} />
                  <div className="text-[14px] font-semibold" style={{ color: 'var(--pro-text-dark)' }}>
                    {formatTND(l.pttc)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Products table (desktop) */}
        <div className="hidden md:block overflow-x-auto" style={{ borderTop: '1px solid var(--pro-line)' }}>
          <table className="w-full text-[13px]">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--pro-line)' }}>
                <Th>{t('pro.devis.col_products', 'Produits')}</Th>
                <Th align="center">{t('pro.devis.col_qty', 'Qté')}</Th>
                <Th align="right">{t('pro.devis.col_price', 'Prix')}</Th>
                <Th align="right">{t('pro.devis.col_total', 'Total')}</Th>
                <Th align="center">{t('pro.devis.col_action', 'Action')}</Th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => {
                const art = articleMap.get(l.articleId);
                return (
                  <tr key={l.id} style={{ borderBottom: '1px solid var(--pro-line)' }}>
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-12 h-12 rounded-lg shrink-0 flex items-center justify-center overflow-hidden"
                          style={{ background: 'var(--pro-grey-subtle)' }}
                        >
                          {art?.imageUrl ? (
                            <img src={art.imageUrl} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Package className="w-5 h-5" style={{ color: 'var(--pro-text-grey-light)' }} />
                          )}
                        </div>
                        <div className="min-w-0">
                          <div
                            className="text-[14px] font-medium truncate"
                            style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
                          >
                            {art?.name ?? l.articleName ?? t('pro.devis.deleted_article', 'Article supprime')}
                          </div>
                          <div
                            className="text-[12px] truncate"
                            style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
                          >
                            {art?.category ?? ''}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <QtyStepper qty={l.quantite} onDelta={(d) => setQty(l.id, d)} />
                    </td>
                    <td className="py-3 px-3 text-right" style={{ color: 'var(--pro-text-dark)' }}>
                      {formatTND(l.unitPriceTTC)}
                    </td>
                    <td className="py-3 px-3 text-right font-medium" style={{ color: 'var(--pro-text-dark)' }}>
                      {formatTND(l.pttc)}
                    </td>
                    <td className="py-3 px-3 text-center">
                      <button
                        type="button"
                        onClick={() => removeLine(l.id)}
                        aria-label={t('pro.devis.remove_line', 'Supprimer la ligne')}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg"
                        style={{
                          border: '1px solid var(--pro-line)',
                          color: 'var(--pro-danger)',
                          background: 'white',
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Add product */}
        <div className="mt-4 relative">
          <button
            type="button"
            onClick={() => {
              setShowPicker((s) => !s);
              setPickerQuery('');
            }}
            className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-lg text-[14px] font-medium"
            style={{
              border: '1px solid var(--pro-line)',
              color: 'var(--pro-text-dark)',
              background: 'white',
              fontFamily: 'Figtree',
            }}
          >
            <Plus className="w-4 h-4" />
            {t('pro.devis.add_product', 'Ajouter un produit')}
          </button>
          {showPicker && (
            <div
              className="absolute left-0 right-0 mt-2 max-h-[320px] overflow-y-auto rounded-lg z-20 pro-fade-in"
              style={{ border: '1px solid var(--pro-border)', background: 'white' }}
            >
              <div
                className="sticky top-0 p-2 bg-white"
                style={{ borderBottom: '1px solid var(--pro-line)' }}
              >
                <input
                  type="search"
                  autoFocus
                  value={pickerQuery}
                  onChange={(e) => setPickerQuery(e.target.value)}
                  placeholder={t('pro.devis.search_product', 'Rechercher un produit…')}
                  className="w-full h-9 px-3 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-orange-300"
                  style={{ background: 'var(--pro-grey-subtle)', color: 'var(--pro-text-dark)' }}
                />
              </div>
              {pickerResults.length === 0 && (
                <div className="px-3 py-4 text-[13px]" style={{ color: 'var(--pro-text-grey)' }}>
                  {t('pro.devis.picker_no_results', 'Aucun produit trouvé')}
                </div>
              )}
              {pickerResults.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => addLine(a.id)}
                  className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-orange-50/40"
                >
                  <div
                    className="w-9 h-9 rounded-md shrink-0 flex items-center justify-center overflow-hidden"
                    style={{ background: 'var(--pro-grey-subtle)' }}
                  >
                    {a.imageUrl ? (
                      <img src={a.imageUrl} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Package className="w-4 h-4" style={{ color: 'var(--pro-text-grey-light)' }} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] truncate" style={{ color: 'var(--pro-text-dark)' }}>
                      {a.name}
                    </div>
                    <div className="text-[11px]" style={{ color: 'var(--pro-text-grey)' }}>
                      {formatTND(a.salePriceHt * (1 + a.vatRate / 100))}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Produits demandés — la réponse du fournisseur, demande par demande.
            Lecture seule côté électricien : la décision n'est pas la sienne. */}
        {productRequests.length > 0 && (
          <div
            className="mt-6 rounded-xl overflow-hidden"
            style={{ border: '1px solid var(--pro-line)' }}
          >
            <div
              className="px-4 py-3 flex items-center gap-2 flex-wrap"
              style={{
                background: 'var(--pro-grey-subtle)',
                borderBottom: '1px solid var(--pro-line)',
              }}
            >
              <PackageSearch
                className="w-4 h-4 shrink-0"
                style={{ color: 'var(--pro-primary)' }}
              />
              <span
                className="text-[14px] font-semibold"
                style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
              >
                {t('pro.request.section', 'Produits demandés')}
              </span>
              <span
                className="text-[12px] px-2 py-0.5 rounded-full font-medium"
                style={
                  requestSummary.hasPending
                    ? {
                        background: 'var(--pro-primary-subtle)',
                        color: 'var(--pro-primary-text)',
                      }
                    : {
                        background: 'rgba(29,175,97,0.12)',
                        color: 'var(--pro-success)',
                      }
                }
              >
                {requestSummary.hasPending
                  ? t('pro.request.awaiting_reply', 'En attente de réponse')
                  : t('pro.request.answered', 'Traité')}
              </span>
            </div>

            <div className="divide-y" style={{ borderColor: 'var(--pro-line)' }}>
              {productRequests.map((request) => (
                <div key={request.id} className="p-4 flex flex-col gap-2">
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="flex-1 min-w-[180px]">
                      <div
                        className="text-[14px] font-medium break-words"
                        style={{
                          color: 'var(--pro-text-dark)',
                          fontFamily: 'Figtree',
                        }}
                      >
                        {request.label}
                      </div>
                      <div
                        className="text-[12px] mt-0.5"
                        style={{ color: 'var(--pro-text-grey)' }}
                      >
                        {t('pro.request.quantity', 'Quantité')} :{' '}
                        {request.quantity}
                        {request.note ? ` · ${request.note}` : ''}
                      </div>
                    </div>
                    <span
                      className="text-[12px] px-2.5 py-1 rounded-full font-semibold shrink-0"
                      style={REQUEST_STATUS_STYLE[request.status]}
                    >
                      {t(REQUEST_STATUS_KEY[request.status], REQUEST_STATUS_FALLBACK[request.status])}
                    </span>
                  </div>

                  {request.status === 'fulfilled' && request.resolvedArticleName && (
                    <div
                      className="rounded-lg px-3 py-2 text-[13px] flex items-center gap-2 flex-wrap"
                      style={{
                        background: 'rgba(29,175,97,0.08)',
                        color: 'var(--pro-text-dark)',
                      }}
                    >
                      <Check
                        className="w-4 h-4 shrink-0"
                        style={{ color: 'var(--pro-success)' }}
                      />
                      <span className="break-words">
                        {t('pro.request.replaced_by', 'Remplacé par')} :{' '}
                        <strong>{request.resolvedArticleName}</strong>
                        {request.resolvedArticleCode
                          ? ` (${request.resolvedArticleCode})`
                          : ''}
                      </span>
                    </div>
                  )}

                  {request.resolutionNote && (
                    <div
                      className="text-[12px] break-words"
                      style={{ color: 'var(--pro-text-grey)' }}
                    >
                      {request.resolutionNote}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Summary */}
        <div className="flex justify-end mt-6">
          <div
            className="w-full max-w-[400px] rounded-xl p-4 space-y-3"
            style={{ border: '1px solid var(--pro-line)', background: 'var(--pro-bg)' }}
          >
            <div className="flex items-center justify-between">
              <span
                className="text-[14px]"
                style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
              >
                {t('pro.devis.discount', 'Remise')}
              </span>
              <RemiseToggle current={remiseTier} onChange={setRemiseTier} />
            </div>
            <div className="flex items-center justify-between pt-3" style={{ borderTop: '1px solid var(--pro-line)' }}>
              <span
                className="text-[14px] font-semibold"
                style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
              >
                {t('pro.devis.total_amount', 'Montant total')}
              </span>
              <span
                className="text-[16px] font-bold"
                style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
              >
                {formatTND(totals.pttc)}
              </span>
            </div>
            {devis.totalCommission > 0 && (
              <div
                className="flex items-center justify-between rounded-lg px-3 py-2"
                style={{ background: 'var(--pro-primary-subtle)' }}
              >
                <span
                  className="text-[14px] font-medium"
                  style={{ color: 'var(--pro-primary)', fontFamily: 'Figtree' }}
                >
                  {t('pro.devis.your_commission', 'Votre Commission')}
                </span>
                <span
                  className="text-[14px] font-semibold"
                  style={{ color: 'var(--pro-primary)', fontFamily: 'Figtree' }}
                >
                  {formatTND(devis.totalCommission)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function QtyStepper({ qty, onDelta }: { qty: number; onDelta: (d: number) => void }) {
  return (
    <div
      className="inline-flex items-center gap-2 rounded-full px-2 py-1"
      style={{ background: 'var(--pro-primary-subtle)' }}
    >
      <button
        type="button"
        onClick={() => onDelta(-1)}
        className="w-5 h-5 rounded-full inline-flex items-center justify-center"
        style={{ background: 'white', color: 'var(--pro-primary)' }}
        aria-label="Décrémenter"
      >
        <Minus className="w-3 h-3" />
      </button>
      <span className="text-[13px] font-medium min-w-[16px] text-center" style={{ color: 'var(--pro-text-dark)' }}>
        {qty}
      </span>
      <button
        type="button"
        onClick={() => onDelta(1)}
        className="w-5 h-5 rounded-full inline-flex items-center justify-center"
        style={{ background: 'white', color: 'var(--pro-primary)' }}
        aria-label="Incrémenter"
      >
        <Plus className="w-3 h-3" />
      </button>
    </div>
  );
}

function ClientSelect({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
  options: ProClient[];
  placeholder: string;
}) {
  return (
    <Dropdown<string>
      value={value}
      onChange={(id) => onChange(id || null)}
      variant="outline"
      size="lg"
      placeholder={placeholder}
      fullWidth
      options={options.map((c) => ({
        key: c.id,
        label: [c.prenom, c.nom].filter(Boolean).join(' ') || c.nom,
      }))}
    />
  );
}

function RemiseToggle({
  current,
  onChange,
}: {
  current: RemiseTier;
  onChange: (t: RemiseTier) => void;
}) {
  return (
    <div className="inline-flex items-center gap-1">
      {REMISE_TIERS.map((tier) => {
        const active = tier === current;
        return (
          <button
            key={tier}
            type="button"
            onClick={() => onChange(tier)}
            className="inline-flex items-center justify-center h-7 px-3 rounded-lg text-[12px] font-medium select-none"
            style={
              active
                ? {
                    border: '1px solid var(--pro-primary)',
                    color: 'var(--pro-primary-text)',
                    background: 'var(--pro-primary-subtle)',
                  }
                : {
                    border: '1px solid transparent',
                    color: 'var(--pro-text-muted)',
                    background: 'transparent',
                  }
            }
          >
            {tier}
          </button>
        );
      })}
    </div>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'center' | 'right';
}) {
  return (
    <th
      className={`py-3 px-3 text-[12px] font-medium ${
        align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'
      }`}
      style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
    >
      {children}
    </th>
  );
}
